'use client';

import Script from 'next/script';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget for the Supabase Auth captcha.
 *
 * Supabase (Dashboard → Auth → Attack Protection) verifies the token server-side;
 * the app only has to render the widget and pass the token as `captchaToken` on
 * sign-in, sign-up, password-reset and magic-link calls. Tokens are single-use,
 * so call `reset()` after every attempt that keeps the user on the page.
 *
 * Site key resolution: NEXT_PUBLIC_TURNSTILE_SITE_KEY if set; otherwise the
 * OneHub widget's key in production builds (site keys are public and bound to
 * the hostnames allow-listed on the Cloudflare widget), and nothing in `next dev`
 * so local work against a captcha-less Supabase keeps working.
 *
 * If the widget itself fails to load or errors (blocked script, hostname not
 * allow-listed), the form stays submittable: with captcha off in Supabase the
 * request succeeds; with it on, Supabase returns a clear captcha error instead
 * of the user staring at a disabled button.
 */
const ONEHUB_SITE_KEY = '0x4AAAAAAFQo0NLZc5QeI8ZZ';
export const TURNSTILE_SITE_KEY =
  process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || (process.env.NODE_ENV === 'production' ? ONEHUB_SITE_KEY : undefined);
export const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY);

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      'timeout-callback'?: () => void;
      'refresh-expired'?: 'auto' | 'manual' | 'never';
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type TurnstileHandle = { reset: () => void };

type TurnstileProps = {
  /** Names the protected surface (login, register, recover, magic-link). */
  action: string;
  /** Called with a fresh token, or null when it expires / errors / is reset. */
  onToken: (token: string | null) => void;
  /** Widget could not run (script blocked, hostname not allow-listed, challenge error). */
  onError?: () => void;
};

const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(({ action, onToken, onError }, ref) => {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [ready, setReady] = useState(false);

  // The script is shared across auth pages; if it already loaded, don't wait for onReady.
  useEffect(() => {
    if (window.turnstile) setReady(true);
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !ready || !container.current || widgetId.current || !window.turnstile) return;
    widgetId.current = window.turnstile.render(container.current, {
      sitekey: TURNSTILE_SITE_KEY,
      action,
      callback: token => onTokenRef.current(token),
      'expired-callback': () => onTokenRef.current(null),
      'error-callback': () => {
        onTokenRef.current(null);
        onErrorRef.current?.();
      },
      'timeout-callback': () => onTokenRef.current(null),
      'refresh-expired': 'auto',
    });
    return () => {
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [ready, action]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
      onTokenRef.current(null);
    },
  }));

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <>
      <Script
        src={SCRIPT_SRC}
        strategy="afterInteractive"
        onReady={() => setReady(true)}
        onError={() => onErrorRef.current?.()}
      />
      <div ref={container} />
    </>
  );
});
Turnstile.displayName = 'Turnstile';

export default Turnstile;

/** Token state + reset handle for one auth form. `ready` gates the submit button. */
export function useTurnstile() {
  const ref = useRef<TurnstileHandle>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const reset = useCallback(() => ref.current?.reset(), []);
  const onError = useCallback(() => setFailed(true), []);
  return {
    ref,
    token: token ?? undefined,
    setToken,
    onError,
    reset,
    ready: !turnstileEnabled || Boolean(token) || failed,
  };
}
